import React, { FC } from 'react'

import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { CircularProgressbar } from 'react-circular-progressbar'
import { useTranslation } from 'react-i18next'

import { CustomHorizontalChart } from '../../../components/CustomHorizontalChart'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { toggleMediumWidget } from '../DashboardSlice'
import { MoneyValue } from '../MoneyValue'
import { MediumWidgetsName } from '../types'
import { WidgetWrapper } from '../widgetsComponents'

export const BiggestWinnerLoser: FC = () => {
  const { t } = useTranslation()
  const { spacing, palette } = useTheme()
  const dispatch = useAppDispatch()

  const { widgetsData, mediumWidgetsList } = useAppSelector(({ dashboard }) => dashboard)
  const { currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)

  const currency = currentAccount?.currency as string
  const biggestWinner = widgetsData?.biggerWinnerLooser?.winner
    ? Number(widgetsData?.biggerWinnerLooser?.winner)
    : 0
  const biggestLoss = widgetsData?.biggerWinnerLooser?.looser
    ? Number(widgetsData?.biggerWinnerLooser?.looser)
    : 0
  const percentage = (biggestWinner / (biggestWinner + Math.abs(biggestLoss))) * 100
  const factor = biggestWinner & biggestLoss ? Math.abs(biggestWinner / biggestLoss) : NaN

  const handleCheck = () => {
    dispatch(toggleMediumWidget(MediumWidgetsName.biggestWinnerLoser))
  }

  return (
    <WidgetWrapper
      label={t('biggestWinnerLoser', 'Biggest winner/loser')}
      handleCheck={handleCheck}
      isChecked={mediumWidgetsList[MediumWidgetsName.biggestWinnerLoser]}
      explanation={t(
        'biggestWinnerLoserExplanation',
        'Biggest winner refers to the trade with the highest net profit, while the biggest loser represents the trade with the highest net loss',
      )}
    >
      <Box display="flex" alignItems="center" height="100%" gap={spacing(2)}>
        <Box width={spacing(15.5)} height={spacing(15.5)}>
          <CircularProgressbar
            value={isNaN(percentage) ? 0 : percentage}
            text={isNaN(factor) ? '-' : factor.toFixed(1)}
            styles={{
              path: {
                stroke: palette.green[400],
                strokeLinecap: 'butt',
                transition: 'stroke-dashoffset 0.5s ease 0s',
                transformOrigin: 'center center',
              },
              trail: {
                stroke: isNaN(percentage) ? palette.grey[500] : palette.red[400],
                strokeLinecap: 'butt',
                transformOrigin: 'center center',
              },
              text: {
                fill: palette.text.primary,
                fontSize: spacing(3),
                fontWeight: 'bold',
                fontFamily: 'Wix Madefor Display',
                textAnchor: 'middle',
                transform: 'translate(0, 6px)',
              },
              background: {
                fill: palette.cyan[500],
              },
            }}
            strokeWidth={6}
          />
        </Box>
        <Box flex={1} display="flex" justifyContent="center" alignItems="center">
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            gap={spacing(1.5)}
            width="100%"
            justifyContent="center"
          >
            <MoneyValue money={biggestWinner} currency={currency} bigFont />
            <CustomHorizontalChart positive={biggestWinner} negative={biggestLoss} />
            <MoneyValue money={biggestLoss} currency={currency} bigFont />
          </Box>
        </Box>
      </Box>
    </WidgetWrapper>
  )
}
